import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Grid, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { useLocation, useNavigate } from 'react-router-dom';
import { DistributionCenterInventory } from '@api/DistributionCenterInventories/DistributionCenterInventory';
import IconButton from '@mui/material/IconButton';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';
import InventoryService from '@api/DistributionCenterInventories/DistributionCenterInventoryCtrl';
import DistributionCenterInventoryHistoryService from '@api/DistributionCenterInventoryHistory/DistributionCenterInventoryHistoryCtrl';
import { DistributionCenterInventoryHistory } from '@api/DistributionCenterInventoryHistory/DistributionCenterInventoryHistory';
import { useSelector } from 'react-redux';
import { RootReducer } from '@store/index';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import SearchIcon from '@mui/icons-material/Search';

const DistributionCenterInventoryRemoveInputsScreen: React.FunctionComponent = () => {
    const navigation = useNavigate();
    const location = useLocation();
    const { loggedUser } = useSelector((state: RootReducer) => state.auth);
    const inventoryService = useMemo(() => new InventoryService(), []);
    const distributionCenterInventoryHistoryService = useMemo(
        () => new DistributionCenterInventoryHistoryService(),
        []
    );
    const [inventory, setInventory] = useState<DistributionCenterInventory[]>([]);
    const [distributionCenterId, setDistributionCenterId] = useState<string | undefined>(undefined);

    const [filters, setFilters] = useState({
        search: ''
    });

    const getData = async (distCenterId: string) => {
        showLoader();
        try {
            setDistributionCenterId(distCenterId);
            const data = await inventoryService.getInventory(distCenterId);
            setInventory(data.filter((d) => d.stock > 0));
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onRemoveInputs = async () => {
        showLoader();
        try {
            const validItems = inventory.filter((i) => (i.stockOut || 0) > 0);

            if (validItems.length === 0) {
                showAlertError('Agrega insumos para registrar la salida');
                return;
            }

            if (!distributionCenterId || !loggedUser) {
                throw new Error('Falta información requerida');
            }

            const inventoryHistory: DistributionCenterInventoryHistory = {
                distributionCenterId,
                inputs: [],
                date: new Date(),
                userData: loggedUser,
                movementType: 'output'
            };

            for (const item of validItems) {
                try {
                    await inventoryService.deleteInventory({
                        distributionCenterId,
                        input: item.input,
                        stock: item.stockOut || 0
                    });
                    inventoryHistory.inputs.push({
                        input: item.input,
                        quantity: item.stockOut || 0
                    });
                } catch (error) {
                    console.error(`Error al eliminar ${item.input.id}:`, error);
                }
            }

            if (inventoryHistory.inputs.length > 0) {
                await distributionCenterInventoryHistoryService.saveInventoryHistory(inventoryHistory);
            }
            navigation(-1);
        } catch (error: any) {
            showAlertError(error);
        } finally {
            hideLoader();
        }
    };

    const onRemoveStock = useCallback(
        (id?: string) => () => {
            if (id) {
                const inventoryArray = [...inventory];
                const index = inventoryArray.findIndex((i) => i.id === id);
                if (index !== -1) {
                    const stockOut = inventoryArray[index].stockOut || 0;
                    if (stockOut > 0) {
                        inventoryArray[index].stockOut = stockOut - 1;
                    }
                    setInventory(inventoryArray);
                }
            }
        },
        [inventory]
    );

    const onAddStock = useCallback(
        (id?: string) => () => {
            if (id) {
                const inventoryArray = [...inventory];
                const index = inventoryArray.findIndex((i) => i.id === id);
                if (index !== -1) {
                    const stockOut = (inventoryArray[index].stockOut || 0) + 1;
                    if (stockOut <= inventoryArray[index].stock) {
                        inventoryArray[index].stockOut = stockOut;
                        setInventory(inventoryArray);
                    }
                }
            }
        },
        [inventory]
    );

    const getTotal = () => {
        return inventory
            .reduce((a, inventory) => a + (inventory.stockOut || 0) * inventory.input.purchaseCost, 0)
            .toFixed(2);
    };

    useEffect(() => {
        const state = location.state as { distributionCenterId: string } | undefined;
        const distributionCenterId = state?.distributionCenterId;
        if (distributionCenterId) {
            void getData(distributionCenterId);
        }
    }, [location.state]);

    const columns = useMemo<ResponsiveGridColDef<DistributionCenterInventory>[]>(
        () => [
            {
                field: 'name',
                flex: 3,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Insumo
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.input.name}</Typography>
            },
            {
                field: 'availableStock',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Stock disponible
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{row.stock}</Typography>
            },
            {
                field: 'stock',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Stock saliendo
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <IconButton color="secondary" onClick={onRemoveStock(row.id)}>
                            <RemoveIcon />
                        </IconButton>
                        <Typography>{row.stockOut || 0}</Typography>
                        <IconButton color="secondary" onClick={onAddStock(row.id)}>
                            <AddIcon />
                        </IconButton>
                    </Stack>
                )
            },
            {
                field: 'cost',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Costo unitario
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.input.costOfSale}`}</Typography>,
                valueGetter: ({ row }) => row.input.costOfSale
            },
            {
                field: 'total',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Total
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.input.costOfSale * (row.stockOut || 0)}`}</Typography>
            }
        ],
        [onRemoveStock, onAddStock]
    );

    return (
        <Box height="100%" p={4}>
            <Stack component={Card} height="100%">
                <Box>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6} />
                        <Grid item xs={12} md={3}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Buscar categoría"
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <SearchIcon />
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Stack direction="row">
                                <Button variant="contained" color="success" onClick={onRemoveInputs}>
                                    Registrar salidas
                                </Button>
                            </Stack>
                        </Grid>
                        <Grid item xs={12} md={1}>
                            <Typography variant="h6" fontWeight={600}>
                                {`Total: $${getTotal()}`}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
                <ResponsiveDataGrid searchValue={filters.search} rows={inventory} columns={columns} />
            </Stack>
        </Box>
    );
};
export default DistributionCenterInventoryRemoveInputsScreen;
